import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";

import i18n from "@/i18n";
import { telemetry } from "@/lib/telemetry";

// ---------------------------------------------------------------------------
// Proactive token refresh — reads expiry from the JS-readable mygrow_logged_in cookie
// ---------------------------------------------------------------------------

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

type RequestSpan = ReturnType<typeof Sentry.startInactiveSpan>;

interface InstrumentedRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _startTime?: number;
  _normalizedUrl?: string;
  _sentrySpan?: RequestSpan;
  /** Ver `redirectToLogin` — chamadas que só sondam "estou logado?" marcam isto. */
  skipAuthRedirect?: boolean;
}

function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function hasSentry(): boolean {
  return !!import.meta.env.VITE_SENTRY_DSN;
}

function getMethod(config?: Pick<InstrumentedRequestConfig, "method">): string {
  return (config?.method ?? "get").toUpperCase();
}

function normalizeRoute(url?: string): string {
  if (!url) return "unknown";

  let pathname = url;
  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    pathname = url.split("?")[0] ?? url;
  }

  const segments = pathname.split("/");
  const normalizedSegments = segments.map((segment) => {
    if (!segment) return segment;
    if (/^\d+$/.test(segment)) return ":id";
    if (/^[0-9a-f]{8,}(?:-[0-9a-f]{4,}){0,4}$/i.test(segment)) return ":id";
    if (/^[a-z]+-\d{2,}$/i.test(segment)) return ":id";
    if (/^[a-z0-9_-]{16,}$/i.test(segment) && /\d/.test(segment)) return ":id";
    return segment;
  });

  return normalizedSegments.join("/") || "/";
}

function getRequestId(config?: Pick<InstrumentedRequestConfig, "headers">): string | undefined {
  const headers = config?.headers;
  if (!headers) return undefined;
  if (headers instanceof AxiosHeaders) {
    const value = headers.get("x-request-id");
    return typeof value === "string" ? value : undefined;
  }

  const value = headers["x-request-id"] ?? headers["X-Request-Id"];
  return Array.isArray(value) ? value[0] : value;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return String(error);
}

function attachRequestSpan(config: InstrumentedRequestConfig): void {
  if (!hasSentry()) return;

  const method = getMethod(config);
  const normalizedUrl = config._normalizedUrl ?? normalizeRoute(config.url);
  const requestId = getRequestId(config);

  config._sentrySpan = Sentry.startInactiveSpan({
    name: `${method} ${normalizedUrl}`,
    op: "http.client",
    attributes: {
      "http.request.method": method,
      "http.route": normalizedUrl,
      ...(config.url ? { "url.full": config.url } : {}),
      ...(requestId ? { "http.request.header.x_request_id": requestId } : {}),
    },
  });
}

function finalizeRequestSpan(
  config: InstrumentedRequestConfig | undefined,
  outcome: "success" | "error",
  status?: number,
  error?: unknown,
): void {
  const span = config?._sentrySpan;
  if (!span) return;

  const durationMs = config?._startTime ? Date.now() - config._startTime : undefined;
  const requestId = getRequestId(config);
  span.setAttributes({
    "http.request.method": getMethod(config),
    "http.route": config._normalizedUrl ?? normalizeRoute(config.url),
    ...(config.url ? { "url.full": config.url } : {}),
    ...(durationMs != null ? { "http.client.duration_ms": durationMs } : {}),
    ...(requestId ? { "http.request.header.x_request_id": requestId } : {}),
    ...(status != null ? { "http.response.status_code": status } : {}),
  });

  if (status != null) {
    Sentry.setHttpStatus(span, status);
  }

  if (outcome === "error") {
    span.setStatus({ code: 2, message: getErrorMessage(error) });
    span.setAttribute("error", true);
  } else {
    span.setStatus({ code: 1 });
  }

  span.end();
  config._sentrySpan = undefined;
}

function captureRequestDuration(
  config: InstrumentedRequestConfig | undefined,
  status?: number,
): void {
  const startTime = config?._startTime;
  if (!startTime) return;

  telemetry.captureMetric("http.client.duration", Date.now() - startTime, {
    method: getMethod(config),
    url: config._normalizedUrl ?? normalizeRoute(config.url),
    status: String(status ?? "unknown"),
  });
}

export function scheduleProactiveRefresh(expiresInSeconds?: number): void {
  if (refreshTimer) clearTimeout(refreshTimer);

  const expiresIn = expiresInSeconds ?? Number(getCookieValue("mygrow_logged_in") || "0");
  if (!expiresIn) return;

  const refreshIn = Math.max(expiresIn * 1000 - 60_000, 10_000);
  refreshTimer = setTimeout(async () => {
    try {
      await axios.post("/api/auth/refresh", {}, { withCredentials: true });
      scheduleProactiveRefresh();
    } catch (err) {
      telemetry.captureWarning("auth.token_refresh", "Proactive token refresh failed", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, refreshIn);
}

export function cancelProactiveRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Axios client
// ---------------------------------------------------------------------------

const apiClient = axios.create({
  baseURL: "",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach request ID and start time for telemetry
apiClient.interceptors.request.use((config) => {
  const instrumentedConfig = config as InstrumentedRequestConfig;
  const headers = AxiosHeaders.from(config.headers ?? {});

  headers.set("x-request-id", crypto.randomUUID());
  // #906: o idioma da tela vai junto, porque parte do que a API devolve é
  // texto de negócio e não rótulo nosso — o nome do componente de aplicação
  // sai da `TAPLT` do SAP, que tem uma linha por idioma. Sem isto a API cai no
  // padrão e a coluna "Module" fica em inglês para quem lê em português.
  headers.set("Accept-Language", i18n.language || "en-US");
  instrumentedConfig.headers = headers;
  instrumentedConfig._startTime = Date.now();
  instrumentedConfig._normalizedUrl = normalizeRoute(config.url);
  attachRequestSpan(instrumentedConfig);

  return instrumentedConfig;
});

// Um 401 sem sessão válida manda para /login com um reload inteiro da página.
// Duas guardas:
// - `skipAuthRedirect`: GET /api/auth/me feito por AuthGuard/GuestGuard só
//   sonda "estou logado?" — o próprio guard já decide o que fazer com a
//   resposta (renderizar a rota pública ou levar para /login via SPA). Sem
//   esta guarda, todo visitante anônimo em `/` ou `/login` era arrancado da
//   tela por um reload duro antes mesmo de ver a landing page (SIMP.2 #423).
// - já estar em `/login`: se a chamada que falhou partiu de lá, reatribuir a
//   mesma URL recarrega a página, que dispara o mesmo 401 de novo — loop
//   infinito.
function redirectToLogin(config?: InstrumentedRequestConfig): void {
  if (config?.skipAuthRedirect) return;
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

// Handle responses: track latency on success, refresh on 401
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach((prom) => {
    if (!error) {
      prom.resolve();
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => {
    const config = response.config as InstrumentedRequestConfig;
    captureRequestDuration(config, response.status);
    finalizeRequestSpan(config, "success", response.status);
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InstrumentedRequestConfig | undefined;

    captureRequestDuration(originalRequest, error.response?.status);
    finalizeRequestSpan(originalRequest, "error", error.response?.status, error);

    if (error.response?.status !== 401 || originalRequest?._retry) {
      const durationMs = originalRequest?._startTime ? Date.now() - originalRequest._startTime : undefined;
      telemetry.captureError("api", error, {
        url: originalRequest?._normalizedUrl ?? normalizeRoute(originalRequest?.url),
        method: originalRequest?.method,
        status: error.response?.status,
        requestId: getRequestId(originalRequest),
        durationMs,
      });
      return Promise.reject(error);
    }

    // Não tentar renovar se a requisição que falhou já era a própria renovação:
    // seria recursão. O `/api/auth/login` também era testado aqui, e saiu com a
    // rota — o login por senha não existe mais, e a entrada federada é uma
    // navegação de página inteira que nunca passa por este cliente.
    if (originalRequest?.url?.includes("/api/auth/refresh")) {
      redirectToLogin(originalRequest);
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<unknown>((resolve, reject) => {
        failedQueue.push({
          resolve: () =>
            originalRequest ? resolve(apiClient(originalRequest)) : resolve(undefined),
          reject,
        });
      });
    }

    if (!originalRequest) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await axios.post("/api/auth/refresh", {}, { withCredentials: true });
      scheduleProactiveRefresh();
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      redirectToLogin(originalRequest);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
